import { HocuspocusProvider } from '@hocuspocus/provider'
import type { CursorEditor, YHistoryEditor, YjsEditor } from '@slate-yjs/core'
import { withCursors, withYHistory, withYjs } from '@slate-yjs/core'
import { useDecorateRemoteCursors } from '@slate-yjs/react'
import { use, useCallback, useEffect, useMemo, useState } from 'react'
import { type BaseEditor, type Editor } from 'slate'
import * as Y from 'yjs'
import { getJwtToken } from './actions'
import { SharedDocContext } from './SharedDocContext'
import { useYjsConnection } from './useConnection'
import { useMyCursor } from './useMyCursor'

export { useYjsConnection } from './useConnection'
export { useMyCursor } from './useMyCursor'

export type YjsEntityType = 'collection-chapter' | 'story' | 'note'

export type CursorData = {
  color: string
  name: string
  image?: string
}

type ConnectionStatus = 'connected' | 'disconnected' | 'connecting'

export type ExtendedYjsEditor = YjsEditor &
  CursorEditor<CursorData> &
  YHistoryEditor & {
    yjsPluginInstalled?: true
  }

export function withDecorator<T extends BaseEditor>(editor: T) {
  const e = editor as T & Editor
  const { useInitializer } = e

  e.yjsPluginInstalled = true

  e.useInitializer = function useInitializerFunc({ decorators = [], ...rest }) {
    useYjsConnection(e)
    useMyCursor(e)

    const decorator = useDecorateRemoteCursors<CursorData>()
    return useInitializer({ decorators: [...decorators, decorator], ...rest })
  }

  return e
}

export const useYjs = (
  entityType: YjsEntityType,
  entityId: string,
  { initialUpdate }: { initialUpdate?: Uint8Array },
) => {
  const [token, setToken] = useState<string | null>(null)
  const [channel, setChannel] = useState<string | null>(null)

  const [provider, setProvider] = useState<HocuspocusProvider | null>(null)
  const [sharedRoot, setSharedRoot] = useState<Y.XmlText | null>()
  const [, setSharedDoc] = use(SharedDocContext)

  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [synced, setSynced] = useState(false)
  const [hasConnected, setHasConnected] = useState(false)

  // Get jwt auth token and server-generated channel name
  useEffect(() => {
    getJwtToken(entityType, entityId).then(({ token, channel }) => {
      setToken(token)
      setChannel(channel)
    })

    return () => {
      setToken(null)
      setChannel(null)
    }
  }, [entityType, entityId])

  // Construct doc and provider
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialUpdate is an initialization-only option; adding it would destroy and recreate the provider on every render change
  useEffect(() => {
    if (!channel || !token) return

    const yDoc = new Y.Doc()

    if (initialUpdate?.byteLength) {
      Y.applyUpdate(yDoc, initialUpdate)
    }

    const content = yDoc.get('content', Y.XmlText)

    const provider = new HocuspocusProvider({
      url: process.env.NEXT_PUBLIC_YJS_SERVER!,
      name: channel,
      document: yDoc,
      token,
      onStatus: ({ status }) => setStatus(status),
      onSynced: () => {
        setSynced(true)
        setHasConnected(true)
      },
    })

    setSharedRoot(content)
    setSharedDoc(yDoc)
    setProvider(provider)

    return () => {
      provider.destroy()
      yDoc.destroy()

      setSharedRoot(null)
      setSharedDoc(null)
      setProvider(null)

      setSynced(false)
      setStatus('disconnected')
      setHasConnected(false)
    }
  }, [channel, token])

  const enhancedEditor = useCallback(
    (e: Editor) => {
      if (!sharedRoot) {
        throw new Error(
          'Make sure to load editor with Yjs only when the extension is ready',
        )
      }

      let editor = withYjs(e, sharedRoot, { autoConnect: false })

      if (provider?.awareness) {
        editor = withCursors(
          withDecorator(withYHistory(editor)),
          provider.awareness,
        )
      }

      return editor
    },
    [provider?.awareness, sharedRoot],
  )

  return {
    withYjs: enhancedEditor,
    state: {
      sync: synced,
      status: status,
      channel: channel,
      connected: synced && status === 'connected' && Boolean(sharedRoot),
      hasConnected,
    },
    sharedRoot,
    provider,
  }
}

export function useLocalYjs(config: { initialUpdate?: Uint8Array } = {}) {
  const [state, setState] = useState({ connected: false })

  // biome-ignore lint/correctness/useExhaustiveDependencies: config is intentionally omitted — the yDoc must be created exactly once on mount; recreating it would destroy collaborative state
  const sharedRoot = useMemo(() => {
    try {
      const yDoc = new Y.Doc()

      if (config.initialUpdate?.byteLength)
        Y.applyUpdate(yDoc, config.initialUpdate)

      return yDoc.get('content', Y.XmlText)
    } catch (err) {
      console.error('Failed to initialize local Yjs:', err)
    }
  }, [])

  useEffect(() => {
    setState({ connected: true })
  }, [])

  return {
    withLocalYjs: (e: Editor) => {
      if (!sharedRoot) {
        throw new Error(
          'Make sure to load editor with Yjs only when the extension is ready',
        )
      }

      e.yjsPluginInstalled = true

      return withYHistory(withYjs(e, sharedRoot, { autoConnect: true }))
    },
    state,
    sharedRoot,
  }
}
